/**
 * Removes tracker leftovers from Web Storage. vanilla-cookieconsent's
 * autoClear only reaches cookies, but PostHog and Google Ads also write to
 * localStorage/sessionStorage, and a withdrawal has to remove those too
 * (GDPR čl. 7 ods. 3; ePrivacy čl. 5 ods. 3 covers any "information stored in
 * the terminal equipment", not only cookies — #11843).
 */

/** Minimal slice of the Web Storage API, so tests can pass a fake. */
export interface KeyedStorage {
  readonly length: number;
  key: (index: number) => string | null;
  removeItem: (key: string) => void;
}

/** Remove every key in each storage that matches any of `patterns`. */
export function removeStorageKeysMatching(
  storages: readonly KeyedStorage[],
  patterns: readonly RegExp[]
): void {
  for (const storage of storages) {
    const doomed: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key !== null && patterns.some((re) => re.test(key))) doomed.push(key);
    }
    // Collected first: removing while indexing would skip the next key.
    for (const key of doomed) storage.removeItem(key);
  }
}

/**
 * Browser-side wrapper: clears matching keys from localStorage and
 * sessionStorage. Storage access can throw (disabled storage, sandboxed
 * iframe, Safari private mode quota) — a failed cleanup must not break the
 * consent callback, so a DOMException is swallowed per storage.
 */
export function removeBrowserStorageKeysMatching(
  patterns: readonly RegExp[]
): void {
  if (typeof window === 'undefined') return;
  for (const pick of [() => window.localStorage, () => window.sessionStorage]) {
    try {
      removeStorageKeysMatching([pick()], patterns);
    } catch (error) {
      // SecurityError (storage disabled) is a DOMException; anything else is a bug.
      if (!(error instanceof DOMException)) throw error;
    }
  }
}
